import type { RouteLocationRaw } from 'vue-router'

import type { AclAction } from '../acl/can'
import type { BadgeKey } from '../stores/counts'
import type { Session, SoftwareCode, UserType } from '../stores/session'

/** What a nav target may depend on: the menu's software (legacy passed `params: { software }`). */
export interface NavContext {
  software: SoftwareCode
}

export interface NavItem {
  /** Unique across modules. */
  id: string
  /** i18n key (nav titles live in `common.nav.*`). */
  title: string
  /** Feather icon name (legacy nav data: `MonitorIcon`, …). */
  icon?: string
  /** Target; a function receives the menu's software (common modules keep the `/:software` param). */
  to?: RouteLocationRaw | ((context: NavContext) => RouteLocationRaw)
  /** Softwares whose menu shows the item (legacy builds one menu per software, `navigation/vertical/index.js`). */
  softwares: readonly SoftwareCode[]
  /** Restrict to these user types (e.g. legacy: no common section for a Freelancer). */
  userTypes?: readonly UserType[]
  /** ACL resource; items the user cannot open are hidden (D-21). */
  resource?: string
  action?: AclAction
  /** Shown only when `VITE_BETA_FEATURE` is on (legacy "Organisation Chart"). */
  beta?: boolean
  /** Hidden without a current workspace (legacy: Dashboard, and the candidate's Applications/Tasks/Documents). */
  requiresWorkspace?: boolean
  /** Red counter from the badge store (`main/counts`), hidden at 0. */
  badge?: BadgeKey
  /** Position in the menu; ties sort by id. */
  order: number
  children?: NavItem[]
  /**
   * Block of the menu (redesign 2026-10): `menu` (default) holds the software's own entries, `workspace` the entries
   * shared by every employer software (calendar, tasks, documents), `others` the housekeeping ones (organization
   * trash). `account` entries (organization profile, settings) are not menu blocks: the nav's account card lists them
   * in its menu. The nav renders the blocks in `NAV_SECTIONS` order, each under its label.
   */
  section?: NavSection
}

export type NavSection = 'menu' | 'workspace' | 'others' | 'account'
/** The menu blocks, in order; `account` is not one. */
export const NAV_SECTIONS: readonly NavSection[] = ['menu', 'workspace', 'others']

/** The filtered menu split by block, in `NAV_SECTIONS` order; blocks without items are left out. */
export function groupNav(items: NavItem[]): { section: NavSection; items: NavItem[] }[] {
  return NAV_SECTIONS.map((section) => ({
    section,
    items: items.filter((item) => (item.section ?? 'menu') === section),
  })).filter((group) => group.items.length > 0)
}

export type NavModules = Record<string, { default: NavItem[] }>

const discovered = import.meta.glob<{ default: NavItem[] }>('/src/modules/*/nav.ts', { eager: true })

/** Every module's `nav.ts` (default export: `NavItem[]`), ids checked for uniqueness, sorted by `order`. */
export function collectNav(modules: NavModules = discovered): NavItem[] {
  const seen = new Set<string>()
  const items: NavItem[] = []
  for (const file of Object.keys(modules).sort()) {
    const declared = modules[file]?.default
    if (!Array.isArray(declared)) throw new Error(`${file} must export default an array of nav items`)
    for (const item of declared) {
      if (seen.has(item.id)) throw new Error(`Nav item id "${item.id}" is declared twice (${file})`)
      seen.add(item.id)
      items.push(item)
    }
  }
  return sortItems(items)
}

function sortItems(items: NavItem[]): NavItem[] {
  return [...items]
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .map((item) => (item.children ? { ...item, children: sortItems(item.children) } : item))
}

export interface NavFilter {
  software: SoftwareCode | null
  session: Session | null
  can: (resource: string, action: AclAction) => boolean
  betaFeature: boolean
}

/**
 * The menu for one software and user: items of that software, of the user's type, permitted by the ACL (children
 * too — D-21 hides what the user cannot open), beta items only with the flag. A group left without children is
 * dropped.
 */
export function filterNav(items: NavItem[], filter: NavFilter): NavItem[] {
  const { software, session } = filter
  if (!software || !session) return []
  const visible = (item: NavItem): boolean =>
    item.softwares.includes(software) &&
    (!item.userTypes || item.userTypes.includes(session.userType)) &&
    (!item.beta || filter.betaFeature) &&
    (!item.requiresWorkspace || Boolean(session.currentWorkspace)) &&
    (!item.resource || filter.can(item.resource, item.action ?? 'read'))
  const walk = (list: NavItem[]): NavItem[] =>
    list.filter(visible).flatMap((item) => {
      if (!item.children) return [item]
      const children = walk(item.children)
      return children.length ? [{ ...item, children }] : []
    })
  return walk(items)
}

/** The router target of an item for the menu's software. */
export function navTarget(item: NavItem, software: SoftwareCode): RouteLocationRaw | undefined {
  return typeof item.to === 'function' ? item.to({ software }) : item.to
}
